import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const DEFAULTS = {
  siteName: 'Kanzlei Nabhan Gäbler & Partner',
  defaultTitle: 'Rechtsanwalt & Steuerberater Bonn | Kanzlei Nabhan Gäbler & Partner',
  defaultDescription:
    'Rechtsanwälte & Steuerberater in Bonn – bundesweit tätig. Schwerpunkte: Ausländerrecht, Einbürgerung, Steuerrecht, Strafrecht, Gesellschaftsrecht. Persönlich, mehrsprachig, 100 % digital. Erstberatung 200 €.',
  defaultImage: '/assets/og-image.jpg',
  productionOrigin: 'https://www.kanzlei-partner.com',
};

const LOCALES = [
  { code: 'de', hreflang: 'de' },
  { code: 'en', hreflang: 'en' },
  { code: 'fr', hreflang: 'fr' },
  { code: 'ar', hreflang: 'ar' },
  { code: 'tr', hreflang: 'tr' },
  { code: 'it', hreflang: 'it' },
];

/**
 * SEO meta tags for pages.
 *
 *   <Seo title="..." description="..." path="/some-path" />
 *
 * Optional props:
 *   - image:    OG/Twitter image (absolute or path)
 *   - type:     og:type (default: 'website')
 *   - noindex:  set true to add robots noindex,nofollow
 *   - keywords: meta keywords string
 *   - jsonLd:   single object OR array of objects to embed as application/ld+json
 */
const Seo = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  noindex = false,
  keywords,
  jsonLd,
}) => {
  const { i18n } = useTranslation();

  // Remove the static fallback meta tags from index.html once Helmet takes over,
  // so search engines rendering JS don't see duplicate description/OG tags.
  // (Non-JS crawlers still get the static tags from index.html.)
  useEffect(() => {
    document.querySelectorAll('head [data-static-seo]').forEach((el) => el.remove());
  }, []);

  const lang = i18n.resolvedLanguage || i18n.language || 'de';
  const ogLocale = { de: 'de_DE', en: 'en_US', ar: 'ar_SA', tr: 'tr_TR', fr: 'fr_FR' }[lang] || 'de_DE';
  const fullTitle = title || DEFAULTS.defaultTitle;
  const desc = description || DEFAULTS.defaultDescription;

  // Use the production origin for canonical/og URLs so that previews don't
  // pollute Google with preview URLs. Falls back to runtime origin client-side.
  const origin = DEFAULTS.productionOrigin;
  const url = `${origin}${path}`;
  const img = image
    ? (image.startsWith('http') ? image : `${origin}${image}`)
    : `${origin}${DEFAULTS.defaultImage}`;

  const jsonLdArr = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />

      {/* hreflang alternates for multilingual SEO */}
      {!noindex && LOCALES.map((loc) => (
        <link
          key={loc.code}
          rel="alternate"
          hrefLang={loc.hreflang}
          href={url}
        />
      ))}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={url} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Additional crawler hints */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="geo.region" content="DE-NW" />
      <meta name="geo.placename" content="Bonn" />
      <meta name="geo.position" content="50.711945;7.124731" />
      <meta name="ICBM" content="50.711945, 7.124731" />

      {/* Custom JSON-LD passed via prop */}
      {jsonLdArr.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
